import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { customChoiceDialogEventName } from './custom-choice-dialog-event';
import { CustomChoiceDialog } from './custom-choice-dialog';
import { useModalsDialogsContext } from './modals-dialogs-context';
/**
 * Monotonic counter used to give every custom-choice dialog a unique id. The legacy dialog helpers
 * gave each dialog its own UUID so several could stack and close independently; reusing a single id
 * (as a shared `useGenericDialog` instance would) makes `hideModalDialog` close the wrong one when
 * two are open at once.
 */
let customChoiceDialogCounter = 0;
/**
 * Listens for the native browser custom-choice dialog `CustomEvent` and renders the requested
 * dialog through the modal/dialog context, settling the dispatcher's promise with the value of the
 * choice the user picked.
 *
 * Mount this **exactly once**, as a child of the root `ModalDialogProvider`, so any code can
 * request a custom-choice dialog via `dispatchCustomChoiceDialog` without wiring anything up.
 * It must not be nested inside per-modal providers, otherwise a single event would open duplicate
 * dialogs. Returns no UI itself — the dialog is rendered by the modal/dialog context.
 */
export const CustomChoiceDialogListener = () => {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    React.useEffect(() => {
        const handleCustomChoiceDialog = (event) => {
            const { resolve, title, description, children, choices } = event.detail;
            const id = `custom-choice-dialog-${(customChoiceDialogCounter += 1)}`;
            const descriptionId = `${id}-description`;
            const settle = (value) => {
                resolve(value);
                hideModalDialog(id);
            };
            showModalDialog({
                id,
                type: 'dialog',
                // Without a description there is no element to point at.
                'aria-describedby': description !== undefined && description !== null ? descriptionId : undefined,
                element: (_jsx(CustomChoiceDialog, { title: title, description: description, descriptionId: descriptionId, choices: choices.map(choice => ({
                        text: choice.text,
                        variant: choice.variant,
                        e2eId: choice.e2eId,
                        onClick: () => settle(choice.value),
                    })), children: children })),
            });
        };
        window.addEventListener(customChoiceDialogEventName, handleCustomChoiceDialog);
        return () => {
            window.removeEventListener(customChoiceDialogEventName, handleCustomChoiceDialog);
        };
    }, [hideModalDialog, showModalDialog]);
    return null;
};
